<template>
  <div class="min-h-screen" :class="isDark ? 'bg-gray-900' : 'bg-gray-50'">
    <!-- 全屏模式 -->
    <div v-if="isFullscreen" class="fixed inset-0 z-50 flex flex-col overflow-hidden" :class="isDark ? 'bg-gray-900' : 'bg-gray-50'">
      <!-- 顶部工具栏 -->
      <div class="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
        <div class="flex items-center space-x-4">
          <span class="text-lg font-semibold" :class="isDark ? 'text-white' : 'text-gray-800'">
            {{ editingPost ? '编辑文章' : '写文章' }}
          </span>
        </div>
        <div class="flex items-center space-x-3">
          <button @click="saveDraft" 
                  class="px-4 py-2 rounded-lg transition-all duration-300"
                  :class="isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'">
            保存草稿
          </button>
          <button @click="savePost" 
                  class="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-300">
            {{ editingPost ? '更新文章' : '发布文章' }}
          </button>
        </div>
      </div>
      
      <!-- 模式切换栏 -->
      <div class="flex items-center justify-end px-6 py-2 border-b flex-shrink-0" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'">
        <div class="flex items-center space-x-1">
          <button @click="editorMode = 'edit'" 
                  title="编辑模式 (Ctrl+E)"
                  class="flex items-center justify-center px-3 py-2 rounded-lg transition-all duration-300"
                  :class="editorMode === 'edit' 
                    ? (isDark ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-500 hover:bg-gray-600 text-white')
                    : (isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button @click="editorMode = 'preview'" 
                  title="预览模式 (Ctrl+P)"
                  class="flex items-center justify-center px-3 py-2 rounded-lg transition-all duration-300"
                  :class="editorMode === 'preview' 
                    ? (isDark ? 'bg-gray-600 text-white' : 'bg-gray-500 text-white')
                    : (isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button @click="editorMode = 'split'" 
                  title="分屏模式 (Ctrl+D)"
                  class="flex items-center justify-center px-3 py-2 rounded-lg transition-all duration-300"
                  :class="editorMode === 'split' 
                    ? (isDark ? 'bg-gray-600 text-white' : 'bg-gray-500 text-white')
                    : (isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
          <button @click="toggleFullscreen" 
                  title="退出全屏 (Ctrl+F)"
                  class="flex items-center justify-center px-3 py-2 rounded-lg transition-all duration-300"
                  :class="isFullscreen 
                    ? (isDark ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-500 hover:bg-gray-600 text-white')
                    : (isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 12h4M20 12h-4M12 4v4M12 20v-4M4 4l4 4M20 4l-4 4M4 20l4-4M20 20l-4-4" />
            </svg>
          </button>
        </div>
      </div>
      
      <!-- 编辑区域 -->
      <div class="flex-1 p-4 overflow-hidden">
        <!-- 编辑模式 -->
        <div v-if="editorMode === 'edit'" class="h-full rounded-xl shadow-sm overflow-hidden border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
          <textarea ref="editTextareaRef" v-model="editorPost.content" 
                    placeholder="开始编写你的文章...&#10;&#10;支持 Markdown 语法：&#10;- **粗体** *斜体* ~~删除线~~&#10;- # 标题&#10;- ```代码块&#10;- $数学公式$&#10;- - 列表&#10;- > 引用&#10;- | 表格 |"
                    rows="25"
                    @wheel="handleWheelEvent"
                    @input="handleContentChange"
                    @paste="handlePaste"
                    class="w-full h-full p-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none font-mono text-sm leading-relaxed overflow-auto"
                    :class="isDark ? 'bg-gray-900 text-white placeholder-gray-500' : 'bg-gray-50 text-gray-800 placeholder-gray-400'" />
        </div>
        
        <!-- 预览模式 -->
        <div v-else-if="editorMode === 'preview'" class="h-full rounded-xl shadow-sm overflow-auto border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
          <div class="prose max-w-none markdown-content p-4" 
               :class="isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'"
               v-html="renderMarkdown(editorPost.content)"></div>
        </div>
        
        <!-- 分屏模式 -->
        <div v-else class="flex h-full gap-3">
          <!-- 编辑器 -->
          <div ref="editorContainerRef" class="flex-1 min-w-[20%] max-w-[80%] rounded-xl shadow-sm overflow-hidden border" 
               :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'"
               :style="{ flexBasis: editorWidth + '%' }">
            <textarea ref="splitEditorTextareaRef" v-model="editorPost.content" placeholder="开始编写你的文章..."
                      rows="25"
                      @scroll="handleSplitEditorScroll"
                      @wheel="handleWheelEvent"
                      @input="handleContentChange"
                      @paste="handlePaste"
                      class="w-full h-full p-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none font-mono text-sm leading-relaxed overflow-auto"
                      :class="isDark ? 'bg-gray-900 text-white placeholder-gray-500' : 'bg-gray-50 text-gray-800 placeholder-gray-400'" />
          </div>
          
          <!-- 分隔条 -->
          <div class="w-1 cursor-col-resize flex items-center justify-center hover:bg-green-500 transition-colors flex-shrink-0"
               :class="isDark ? 'bg-gray-600' : 'bg-gray-300'"
               @mousedown="startResize">
            <div class="w-1 h-10 rounded-full bg-green-500"></div>
          </div>
          
          <!-- 预览 -->
          <div ref="previewContainerRef" class="flex-1 min-w-[20%] max-w-[80%] rounded-xl shadow-sm overflow-auto border" 
               :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'"
               :style="{ flexBasis: (100 - editorWidth) + '%' }">
            <div class="prose max-w-none markdown-content p-4" 
                 :class="isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'"
                 v-html="renderMarkdown(editorPost.content)"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 正常模式 -->
    <div v-else class="max-w-6xl mx-auto px-6 py-8">
      <div class="flex items-center justify-between mb-8">
        <div class="flex items-center space-x-4">
          <button @click="goBack" 
                  class="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                  :class="isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-600 shadow-sm'">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>返回</span>
          </button>
          <h1 class="text-2xl font-bold" :class="isDark ? 'text-white' : 'text-gray-800'">
            {{ editingPost ? '编辑文章' : '写文章' }}
          </h1>
        </div>
        <div class="flex items-center space-x-3">
          <button @click="saveDraft" 
                  class="px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                  :class="isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'">
            保存草稿
          </button>
          <button @click="savePost" 
                  class="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-green-500/25">
            {{ editingPost ? '更新文章' : '发布文章' }}
          </button>
        </div>
      </div>

      <div class="grid lg:grid-cols-12 gap-6">
        <div class="lg:col-span-9 space-y-6">
          <input v-model="editorPost.title" type="text" placeholder="输入文章标题..."
                 class="w-full px-6 py-4 text-xl font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-300"
                 :class="isDark ? 'bg-gray-800 text-white placeholder-gray-500' : 'bg-white text-gray-800 placeholder-gray-400 shadow-sm'" />

          <div class="rounded-xl shadow-sm overflow-hidden border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'" @wheel="handleWheelEvent">
            <div class="flex items-center justify-end p-2 border-b" :class="isDark ? 'border-gray-700' : 'border-gray-200'">
              <div class="flex items-center space-x-1">
                <button @click="editorMode = 'edit'" 
                        title="编辑模式 (Ctrl+E)"
                        class="flex items-center justify-center px-3 py-2 rounded-lg transition-all duration-300"
                        :class="editorMode === 'edit' 
                          ? (isDark ? 'bg-gray-600 text-white' : 'bg-gray-500 text-white')
                          : (isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button @click="editorMode = 'preview'" 
                        title="预览模式 (Ctrl+P)"
                        class="flex items-center justify-center px-3 py-2 rounded-lg transition-all duration-300"
                        :class="editorMode === 'preview' 
                          ? (isDark ? 'bg-gray-600 text-white' : 'bg-gray-500 text-white')
                          : (isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button @click="editorMode = 'split'" 
                        title="分屏模式 (Ctrl+D)"
                        class="flex items-center justify-center px-3 py-2 rounded-lg transition-all duration-300"
                        :class="editorMode === 'split' 
                          ? (isDark ? 'bg-gray-600 text-white' : 'bg-gray-500 text-white')
                          : (isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button @click="toggleFullscreen" 
                        title="全屏模式 (Ctrl+F)"
                        class="flex items-center justify-center px-3 py-2 rounded-lg transition-all duration-300"
                        :class="isFullscreen 
                          ? (isDark ? 'bg-gray-600 text-white' : 'bg-gray-500 text-white')
                          : (isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')">
                  <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 12h4M20 12h-4M12 4v4M12 20v-4M4 4l4 4M20 4l-4 4M4 20l4-4M20 20l-4-4" />
                  </svg>
                </button>
              </div>
            </div>

            <div v-if="editorMode === 'edit'" class="p-3 h-[60vh] overflow-hidden overscroll-contain" @wheel="handleWheelEvent">
              <textarea ref="editTextareaRef" v-model="editorPost.content" 
                        placeholder="开始编写你的文章...&#10;&#10;支持 Markdown 语法：&#10;- **粗体** *斜体* ~~删除线~~&#10;- # 标题&#10;- ```代码块&#10;- $数学公式$&#10;- - 列表&#10;- > 引用&#10;- | 表格 |"
                        rows="25"
                        @wheel="handleWheelEvent"
                        @input="handleContentChange"
                        @paste="handlePaste"
                        class="w-full h-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none font-mono text-sm leading-relaxed transition-all duration-300 overflow-auto overscroll-contain"
                        :class="isDark ? 'bg-gray-900 text-white placeholder-gray-500' : 'bg-gray-50 text-gray-800 placeholder-gray-400'" />
            </div>

            <div v-else-if="editorMode === 'preview'" class="p-3 h-[60vh] overflow-auto overscroll-contain" @wheel="handleWheelEvent">
              <div class="prose max-w-none markdown-content h-full" 
                   :class="isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'"
                   v-html="renderMarkdown(editorPost.content)"></div>
            </div>

            <div v-else class="flex h-[60vh] overscroll-contain">
              <div ref="editorContainerRef" class="p-3 overflow-hidden overscroll-contain border-r" 
                   :style="{ width: editorWidth + '%' }"
                   :class="isDark ? 'border-gray-700' : 'border-gray-200'"
                   @wheel="handleWheelEvent">
                <textarea ref="splitEditorTextareaRef" v-model="editorPost.content" placeholder="开始编写你的文章..."
                          rows="25"
                          @scroll="handleSplitEditorScroll"
                          @wheel="handleWheelEvent"
                          @input="handleContentChange"
                          @paste="handlePaste"
                          class="w-full h-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none font-mono text-sm leading-relaxed transition-all duration-300 overflow-auto overscroll-contain"
                          :class="isDark ? 'bg-gray-900 text-white placeholder-gray-500' : 'bg-gray-50 text-gray-800 placeholder-gray-400'" />
              </div>
              <div id="resize-handle" 
                   class="w-1 cursor-col-resize flex items-center justify-center hover:bg-green-500 transition-colors"
                   :class="isDark ? 'bg-gray-600' : 'bg-gray-400'"
                   @mousedown="startResize">
                <div class="w-1 h-8 rounded-full bg-green-500"></div>
              </div>
              <div ref="previewContainerRef" class="p-3 overflow-auto overscroll-contain" 
                   :style="{ width: (100 - editorWidth) + '%' }"
                   @scroll="handleSplitPreviewScroll" @wheel="handleWheelEvent">
                <div class="prose max-w-none markdown-content h-full rounded-lg p-4" 
                     :class="isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'"
                     v-html="renderMarkdown(editorPost.content)"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="lg:col-span-3 space-y-6">
          <div class="rounded-xl shadow-sm p-6 border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
            <h3 class="text-lg font-semibold mb-4" :class="isDark ? 'text-white' : 'text-gray-800'">文章设置</h3>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium mb-2" :class="isDark ? 'text-gray-400' : 'text-gray-600'">分类</label>
                <div class="grid grid-cols-2 gap-2">
                  <button v-for="tag in tags" :key="tag"
                          @click="editorPost.category = tag"
                          class="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300"
                          :class="editorPost.category === tag 
                            ? 'bg-green-500 text-white shadow-md' 
                            : (isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-600')">
                    {{ tag }}
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2" :class="isDark ? 'text-gray-400' : 'text-gray-600'">状态</label>
                <select v-model="editorPost.status" 
                        class="w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        :class="isDark ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-800'">
                  <option value="draft">草稿</option>
                  <option value="published">发布</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2" :class="isDark ? 'text-gray-400' : 'text-gray-600'">标签</label>
                <div class="flex flex-wrap gap-2">
                  <span v-for="(tag, index) in selectedTags" :key="index"
                        class="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm"
                        :class="isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'">
                    {{ tag }}
                    <button @click="removeTag(index)" class="hover:text-red-400">
                      <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                </div>
                <input v-model="newTag" @keyup.enter="addTag" 
                       type="text" placeholder="添加标签..."
                       class="mt-2 w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                       :class="isDark ? 'bg-gray-700 text-white placeholder-gray-500' : 'bg-gray-100 text-gray-800 placeholder-gray-400'" />
              </div>
            </div>
          </div>

          <div class="rounded-xl shadow-sm p-6 border" :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
            <h3 class="text-lg font-semibold mb-4" :class="isDark ? 'text-white' : 'text-gray-800'">快捷键</h3>
            <div class="space-y-2 text-sm" :class="isDark ? 'text-gray-400' : 'text-gray-600'">
              <div class="flex items-center justify-between">
                <span>保存</span>
                <kbd class="px-2 py-1 bg-gray-200 rounded text-xs" :class="isDark ? 'bg-gray-700' : 'bg-gray-200'">Ctrl+S</kbd>
              </div>
              <div class="flex items-center justify-between">
                <span>编辑模式</span>
                <kbd class="px-2 py-1 bg-gray-200 rounded text-xs" :class="isDark ? 'bg-gray-700' : 'bg-gray-200'">Ctrl+E</kbd>
              </div>
              <div class="flex items-center justify-between">
                <span>预览模式</span>
                <kbd class="px-2 py-1 bg-gray-200 rounded text-xs" :class="isDark ? 'bg-gray-700' : 'bg-gray-200'">Ctrl+P</kbd>
              </div>
              <div class="flex items-center justify-between">
                <span>分屏模式</span>
                <kbd class="px-2 py-1 bg-gray-200 rounded text-xs" :class="isDark ? 'bg-gray-700' : 'bg-gray-200'">Ctrl+D</kbd>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useTheme } from '../composables/useTheme'
import { useRoute, useRouter } from 'vue-router'
import { marked } from 'marked'
import hljs from 'highlight.js'
import 'highlight.js/styles/github-dark.css'
import katex from 'katex'
import 'katex/dist/katex.min.css'

// Markdown 配置
marked.setOptions({
  breaks: true,
  gfm: true
})

// 自定义渲染器
const renderer = new marked.Renderer()

renderer.code = function({ text, lang }) {
  const language = lang && hljs.getLanguage(lang) ? lang : 'plaintext'
  const highlighted = hljs.highlight(text, { language }).value
  return `<pre><code class="hljs language-${language}">${highlighted}</code></pre>`
}

renderer.text = function({ raw }) {
  return raw
    .replace(/\$\$(.*?)\$\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: true,
          throwOnError: false
        })
      } catch {
        return match
      }
    })
    .replace(/\$(.*?)\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: false,
          throwOnError: false
        })
      } catch {
        return match
      }
    })
}

marked.use({ renderer })

export default {
  name: 'BlogEdit',
  setup() {
    const { isDark } = useTheme()
    const route = useRoute()
    const router = useRouter()
    
    // 响应式状态
    const editorMode = ref('split')
    const editingPost = ref(null)
    const newTag = ref('')
    const selectedTags = ref([])
    const isScrolling = ref(false)
    const editorWidth = ref(50)
    const isResizing = ref(false)
    const isFullscreen = ref(false)
    
    // DOM 引用
    const editTextareaRef = ref(null)
    const splitEditorTextareaRef = ref(null)
    const editorContainerRef = ref(null)
    const previewContainerRef = ref(null)
    
    // 常量数据
    const tags = ['Vue.js', 'React', 'Node.js', 'Python', '其他']
    
    // 编辑器数据
    const editorPost = ref({
      title: '',
      category: 'Vue.js',
      content: '',
      status: 'draft',
      tags: []
    })
    
    /**
     * 渲染 Markdown 内容
     * @param {string} content - Markdown 内容
     * @returns {string} HTML 内容
     */
    const renderMarkdown = (content) => {
      if (!content || !content.trim()) {
        return '<div class="flex items-center justify-center h-full text-gray-500"><p>开始编写你的文章...</p></div>'
      }
      return marked.parse(content)
    }
    
    /**
     * 处理滚轮事件，阻止冒泡到页面
     * @param {WheelEvent} e - 滚轮事件
     */
    const handleWheelEvent = (e) => {
      const target = e.target
      if (!target) return
      
      const isScrollable = target.scrollHeight > target.clientHeight
      
      if (isScrollable) {
        const atBottom = e.deltaY > 0 && target.scrollTop >= target.scrollHeight - target.clientHeight - 1
        const atTop = e.deltaY < 0 && target.scrollTop <= 1
        
        if (atBottom || atTop) {
          e.preventDefault()
        }
      }
      e.stopPropagation()
      e.stopImmediatePropagation()
    }
    
    /**
     * 处理内容变化
     */
    const handleContentChange = () => {
      nextTick(() => {
        const textarea = splitEditorTextareaRef.value || editTextareaRef.value
        if (textarea && textarea.scrollTop > 0) {
          textarea.scrollTop = 0
        }
      })
    }
    
    /**
     * 处理粘贴事件
     */
    const handlePaste = () => {
      setTimeout(() => {
        const textarea = splitEditorTextareaRef.value || editTextareaRef.value
        if (textarea) {
          textarea.scrollTop = 0
        }
        window.scrollTo({ top: 0, behavior: 'instant' })
      }, 0)
    }
    
    /**
     * 开始调整编辑器宽度
     * @param {MouseEvent} e - 鼠标事件
     */
    const startResize = (e) => {
      isResizing.value = true
      document.addEventListener('mousemove', handleResize)
      document.addEventListener('mouseup', stopResize)
      e.preventDefault()
    }
    
    /**
     * 处理宽度调整
     * @param {MouseEvent} e - 鼠标事件
     */
    const handleResize = (e) => {
      if (!isResizing.value) return
      
      let container
      
      if (isFullscreen.value) {
        container = document.querySelector('.fixed.inset-0 .flex.h-full.gap-4')
      } else {
        container = document.querySelector('.flex.h-\\[60vh\\]')
      }
      
      if (!container) return
      
      const containerRect = container.getBoundingClientRect()
      const x = e.clientX - containerRect.left
      const percentage = (x / containerRect.width) * 100
      
      editorWidth.value = Math.max(20, Math.min(80, percentage))
    }
    
    /**
     * 停止宽度调整
     */
    const stopResize = () => {
      isResizing.value = false
      document.removeEventListener('mousemove', handleResize)
      document.removeEventListener('mouseup', stopResize)
    }
    
    /**
     * 处理编辑器滚动（同步到预览）
     */
    const handleSplitEditorScroll = () => {
      if (isScrolling.value) return
      isScrolling.value = true
      
      const editor = splitEditorTextareaRef.value
      const preview = previewContainerRef.value
      
      if (!editor || !preview) {
        isScrolling.value = false
        return
      }
      
      const editorContent = editor.value || ''
      const scrollTop = editor.scrollTop
      const totalLines = editorContent.split('\n').length || 1
      const lineHeight = editor.scrollHeight / totalLines
      const currentLine = Math.floor(scrollTop / lineHeight)
      
      const lines = editorContent.split('\n')
      let charCount = 0
      for (let i = 0; i < Math.min(currentLine, lines.length); i++) {
        charCount += lines[i].length + 1
      }
      
      syncScrollByContent(charCount, editor, preview)
      
      setTimeout(() => {
        isScrolling.value = false
      }, 50)
    }
    
    /**
     * 处理预览滚动（同步到编辑器）
     */
    const handleSplitPreviewScroll = () => {
      if (isScrolling.value) return
      isScrolling.value = true
      
      const editor = splitEditorTextareaRef.value
      const preview = previewContainerRef.value
      
      if (!editor || !preview) {
        isScrolling.value = false
        return
      }
      
      const previewScrollPercent = preview.scrollTop / (preview.scrollHeight - preview.clientHeight)
      editor.scrollTop = previewScrollPercent * (editor.scrollHeight - editor.clientHeight)
      
      setTimeout(() => {
        isScrolling.value = false
      }, 50)
    }
    
    /**
     * 根据内容同步滚动位置
     * @param {number} charPosition - 字符位置
     * @param {HTMLTextAreaElement} editor - 编辑器元素
     * @param {HTMLElement} preview - 预览容器
     */
    const syncScrollByContent = (charPosition, editor, preview) => {
      const previewContent = preview.querySelector('.markdown-content')
      const editorContent = editor.value || ''
      
      if (!previewContent || !editorContent) {
        const editorScrollPercent = editor.scrollHeight > editor.clientHeight 
          ? editor.scrollTop / (editor.scrollHeight - editor.clientHeight) 
          : 0
        preview.scrollTop = editorScrollPercent * (preview.scrollHeight - preview.clientHeight)
        return
      }
      
      const elements = previewContent.querySelectorAll('h1, h2, h3, h4, h5, h6, p, ul, ol, pre, blockquote, table, li')
      
      if (elements.length === 0) {
        const editorScrollPercent = editor.scrollHeight > editor.clientHeight 
          ? editor.scrollTop / (editor.scrollHeight - editor.clientHeight) 
          : 0
        preview.scrollTop = editorScrollPercent * (preview.scrollHeight - preview.clientHeight)
        return
      }
      
      const totalChars = editorContent.length
      const scrollPercent = totalChars > 0 ? charPosition / totalChars : 0
      
      const previewMaxScroll = preview.scrollHeight - preview.clientHeight
      const targetScroll = scrollPercent * previewMaxScroll
      
      preview.scrollTop = Math.max(0, Math.min(targetScroll, previewMaxScroll))
    }
    
    /**
     * 返回博客列表页
     */
    const goBack = () => {
      router.push('/blog')
    }
    
    /**
     * 保存草稿
     */
    const saveDraft = () => {
      if (!editorPost.value.title || editorPost.value.title.trim() === '') {
        alert('请至少输入文章标题')
        return
      }
      localStorage.setItem('draft_post', JSON.stringify(editorPost.value))
      alert('草稿已保存')
    }
    
    /**
     * 保存文章
     */
    const savePost = () => {
      const { title, content } = editorPost.value
      
      if (!title || title.trim() === '') {
        alert('请输入文章标题')
        return
      }
      
      if (!content || content.trim() === '') {
        alert('请输入文章内容')
        return
      }
      
      const posts = JSON.parse(localStorage.getItem('blog_posts') || '[]')
      
      if (editingPost.value) {
        const index = posts.findIndex(p => p.id === editingPost.value.id)
        if (index !== -1) {
          posts[index] = {
            ...posts[index],
            title: title.trim(),
            category: editorPost.value.category,
            content: content.trim(),
            excerpt: content.trim().substring(0, 100) + '...',
            date: new Date().toISOString().split('T')[0],
            tags: [...selectedTags.value]
          }
        }
      } else {
        const newPost = {
          id: posts.length + 1,
          title: title.trim(),
          excerpt: content.trim().substring(0, 100) + '...',
          content: content.trim(),
          category: editorPost.value.category,
          date: new Date().toISOString().split('T')[0],
          author: '潘志捷',
          avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=panzhijie',
          views: 0,
          comments: 0,
          tags: [...selectedTags.value]
        }
        posts.unshift(newPost)
      }
      
      localStorage.setItem('blog_posts', JSON.stringify(posts))
      localStorage.removeItem('draft_post')
      router.push('/blog')
    }
    
    /**
     * 添加标签
     */
    const addTag = () => {
      const trimmedTag = newTag.value.trim()
      if (trimmedTag && !selectedTags.value.includes(trimmedTag)) {
        selectedTags.value.push(trimmedTag)
        newTag.value = ''
      }
    }
    
    /**
     * 移除标签
     * @param {number} index - 标签索引
     */
    const removeTag = (index) => {
      if (index >= 0 && index < selectedTags.value.length) {
        selectedTags.value.splice(index, 1)
      }
    }
    
    /**
     * 切换全屏模式
     */
    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value
      
      if (isFullscreen.value) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
    
    /**
     * 处理键盘快捷键
     * @param {KeyboardEvent} e - 键盘事件
     */
    const handleKeydown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 's':
            e.preventDefault()
            saveDraft()
            break
          case 'e':
            e.preventDefault()
            editorMode.value = 'edit'
            break
          case 'p':
            e.preventDefault()
            editorMode.value = 'preview'
            break
          case 'd':
            e.preventDefault()
            editorMode.value = 'split'
            break
          case 'f':
            e.preventDefault()
            toggleFullscreen()
            break
        }
      }
    }
    
    /**
     * 组件挂载时初始化
     */
    onMounted(() => {
      // 确保页面滚动到顶部
      window.scrollTo({ top: 0, behavior: 'instant' })
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
      }, 100)
      
      // 加载编辑的文章或草稿
      const postId = route.params.id
      if (postId) {
        const posts = JSON.parse(localStorage.getItem('blog_posts') || '[]')
        editingPost.value = posts.find(p => p.id === parseInt(postId))
        if (editingPost.value) {
          editorPost.value = {
            title: editingPost.value.title,
            category: editingPost.value.category,
            content: editingPost.value.content,
            status: 'published',
            tags: editingPost.value.tags || []
          }
          selectedTags.value = editingPost.value.tags || []
        }
      } else {
        const draft = localStorage.getItem('draft_post')
        if (draft) {
          editorPost.value = JSON.parse(draft)
        }
      }
      
      // 添加全局事件监听
      window.addEventListener('keydown', handleKeydown)
    })
    
    /**
     * 组件销毁时清理
     */
    onUnmounted(() => {
      // 移除全局事件监听
      window.removeEventListener('keydown', handleKeydown)
      document.removeEventListener('mousemove', handleResize)
      document.removeEventListener('mouseup', stopResize)
    })
    
    return { 
      isDark,
      editorMode,
      editingPost,
      newTag,
      selectedTags,
      tags,
      editorPost,
      editorWidth,
      isFullscreen,
      editTextareaRef,
      splitEditorTextareaRef,
      editorContainerRef,
      previewContainerRef,
      renderMarkdown,
      handleWheelEvent,
      handleContentChange,
      handlePaste,
      startResize,
      handleResize,
      stopResize,
      toggleFullscreen,
      goBack,
      saveDraft,
      savePost,
      addTag,
      removeTag,
      handleSplitEditorScroll,
      handleSplitPreviewScroll
    }
  }
}
</script>
